import { API_BASE_URL } from "../config";
import { getToken } from "../lib/tokenStorage";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function baseRequest<T>(
  endpoint: string,
  method: HttpMethod,
  body?: unknown,
  init?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const token = getToken();
  const isFormData = body instanceof FormData;
  const headers: Record<string, string> = {
    ...(body && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(init?.headers as Record<string, string> | undefined),
  };

  const res = await fetch(url, {
    method,
    headers,
    body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
    ...init,
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const message = (isJson && (data as any)?.message) || `HTTP ${res.status}`;
    throw new Error(message);
  }

  return (data as T) ?? ({} as T);
}

export const api = {
  get: <T>(endpoint: string, init?: RequestInit) =>
    baseRequest<T>(endpoint, "GET", undefined, init),
  post: <T>(endpoint: string, body?: unknown, init?: RequestInit) =>
    baseRequest<T>(endpoint, "POST", body, init),
  put: <T>(endpoint: string, body?: unknown, init?: RequestInit) =>
    baseRequest<T>(endpoint, "PUT", body, init),
  patch: <T>(endpoint: string, body?: unknown, init?: RequestInit) =>
    baseRequest<T>(endpoint, "PATCH", body, init),
  delete: <T>(endpoint: string, init?: RequestInit) =>
    baseRequest<T>(endpoint, "DELETE", undefined, init),
  upload: <T>(endpoint: string, formData: FormData, init?: RequestInit) =>
    baseRequest<T>(endpoint, "POST", formData, init),
};
